import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

// Importaciones de entornos.
import { environment } from '../../environments/environment';

// Importaciones de interfaces.
import { IConfiguration } from '../interfaces/configuration.interface';

/**
 * Servicio encargado de gestionar la configuración de la aplicación.
 *
 * Este servicio se proporciona a nivel global (`providedIn: 'root'`), lo que lo convierte
 * en un singleton accesible desde cualquier parte de la aplicación.
 *
 * Generalmente se utiliza para cargar, almacenar y acceder a valores de configuración
 * como parámetros globales u opciones específicas del sistema.
 */
@Injectable({
  providedIn: 'root',
})
export class ConfigurationService {
  /**
   * URL del endpoint para acceder a las configuraciones.
   *
   * Esta propiedad almacena la URL a la que se enviarán las solicitudes HTTP
   * para obtener la lista de configuraciones disponibles en la aplicación.
   */
  private url: string = '/configurations';

  /**
   * Constructor de la clase ConfigurationService.
   *
   * Inyecta el servicio `HttpClient`, que se utiliza para realizar solicitudes HTTP
   * al servidor de la API y gestionar las operaciones relacionadas con las configuraciones.
   *
   * @param http - Servicio de Angular para realizar solicitudes HTTP.
   */
  constructor(private http: HttpClient) {}

  /**
   * Obtiene todas las configuraciones disponibles.
   *
   * Realiza una solicitud GET al endpoint de configuraciones y devuelve una promesa
   * que se resuelve con la respuesta del servidor, que contiene la lista de las configuraciones.
   *
   * @returns Una promesa que devuelve los datos de las configuraciones.
   */
  public getAll(): Promise<any> {
    return firstValueFrom(this.http.get(environment.apiUrl + this.url));
  }

  /**
   * Crea o actualiza una configuración.
   *
   * Esta función realiza una solicitud HTTP PUT al servidor para crear o actualizar
   * una configuración específica. Si la configuración ya existe, se actualiza; de lo
   * contrario, se crea una nueva.
   *
   * @param configuration - Objeto que contiene la información de la configuración
   * a ser creada o actualizada, incluyendo su ID y valor.
   *
   * @returns Una promesa que se resuelve con la respuesta del servidor después de la operación.
   */
  upsert(configuration: IConfiguration): Promise<any> {
    return firstValueFrom(
      this.http.put(`${environment.apiUrl + this.url}`, {
        name: configuration.name,
        value: configuration.value,
      })
    );
  }

  /**
   * Crea o actualiza la contraseña de la configuración del correo electrónico.
   *
   * Realiza una solicitud HTTP PATCH para crear o actualizar la contraseña
   * del correo electrónico en el servidor.
   *
   * @param password - Nueva contraseña a ser creada o actualizada.
   *
   * @returns Una promesa que se resuelve con la respuesta del servidor después de la operación.
   */
  upsertEmailPassword(password: string): Promise<any> {
    return firstValueFrom(
      this.http.patch(`${environment.apiUrl + this.url}/email/password`, {
        password,
      })
    );
  }

  /**
   * Eliminar una configuración.
   *
   * Realiza una solicitud HTTP DELETE para eliminar la configuración basado en su ID.
   *
   * @param configuration - Objeto que contiene la información de la configuración con los cambios a ser eliminado.
   *
   * @returns Una promesa que se resuelve con la respuesta del servidor después de la eliminación de la configuración.
   */
  delete(configuration: IConfiguration): Promise<any> {
    return firstValueFrom(
      this.http.delete(`${environment.apiUrl + this.url}/${configuration.id}`)
    );
  }
}
